import React from 'react'

import type { SchemaQuota, SchemaQuotaAdd, SchemaQuotaEdit } from '@/__generated/es-api'
import { useItemContext } from '@/app/module/campaigns/component/item-provider'
import { usePersonalizationList } from '@/app/module/campaigns/hooks/use-personalization-list'
import type { CampaignItem } from '@/app/module/campaigns/types'
import { useCreateQuota } from '@/app/module/content/hooks/use-create-quota'
import { useGetQuotas } from '@/app/module/content/hooks/use-get-quotas'
import { useUpdateQuota } from '@/app/module/content/hooks/use-update-quota'
import type { Condition } from '@/app/types'

import Quotas from './quotas'

type Props = {
  item: CampaignItem
  itemId?: number
}

const QuotasContainer: React.FC<Props> = ({ item, itemId }) => {
  const { data, isFetching } = useGetQuotas(itemId)
  const { setItem } = useItemContext() as { setItem?: (item: CampaignItem) => void }
  const personalizationList = usePersonalizationList()
  const [createQuota, { isLoading: isCreatingQuota }] = useCreateQuota()
  const [updateQuota, { isLoading: isUpdatingQuota }] = useUpdateQuota()
  const campaignCountsByQuotaId = React.useMemo(
    () => Object.fromEntries((data?.quotas ?? []).map(({ campaigns, id }) => [id, campaigns])),
    [data?.quotas],
  )

  const handleAddOrganizationQuota = React.useCallback(
    (quota: SchemaQuota) => {
      if (!setItem) {
        return false
      }

      const existingQuotas = Array.isArray(item.quotas) ? item.quotas : []
      if (existingQuotas.some(({ quotaId }) => quotaId === quota.id)) {
        return false
      }

      setItem({
        ...item,
        quotas: [
          ...existingQuotas,
          {
            conditions: [],
            quotaGoal: quota.goal,
            quotaHit: quota.hit,
            quotaId: quota.id,
            quotaName: quota.name,
            quotaUsed: quota.used,
          },
        ],
      })

      return true
    },
    [item, setItem],
  )

  const handleCreateQuota = React.useCallback(
    async (quota: SchemaQuotaAdd, conditions: Condition | null) => {
      if (!setItem) {
        return false
      }

      const existingQuotas = Array.isArray(item.quotas) ? item.quotas : []
      const createdQuota = await createQuota(quota)
      if (!createdQuota) {
        return false
      }

      setItem({
        ...item,
        quotas: [
          ...existingQuotas,
          {
            conditions: conditions ?? [],
            quotaGoal: quota.goal,
            quotaHit: false,
            quotaId: createdQuota.id,
            quotaName: createdQuota.name,
            quotaUsed: 0,
          },
        ],
      })

      return true
    },
    [item, createQuota, setItem],
  )

  const handleDeleteQuota = React.useCallback(
    (quotaId: string) => {
      if (!setItem || !Array.isArray(item.quotas)) {
        return
      }

      setItem({
        ...item,
        quotas: item.quotas.filter((quota) => quota.quotaId !== quotaId),
      })
    },
    [item, setItem],
  )

  const handleSaveConditions = React.useCallback(
    (quotaId: string, conditions: Condition | null) => {
      if (!setItem || !Array.isArray(item.quotas)) {
        return
      }

      setItem({
        ...item,
        quotas: item.quotas.map((quota) =>
          quota.quotaId === quotaId ? { ...quota, conditions: conditions ?? [] } : quota,
        ),
      })
    },
    [item, setItem],
  )

  const handleSaveMetadata = React.useCallback(
    async (quotaId: string, quota: Required<SchemaQuotaEdit>) => {
      if (!setItem || !Array.isArray(item.quotas)) {
        return false
      }

      const success = await updateQuota(quotaId, quota)
      if (!success) {
        return false
      }

      setItem({
        ...item,
        quotas: item.quotas.map((itemQuota) =>
          itemQuota.quotaId === quotaId ? { ...itemQuota, quotaGoal: quota.goal, quotaName: quota.name } : itemQuota,
        ),
      })
      return true
    },
    [item, setItem, updateQuota],
  )

  const handleSaveQuota = React.useCallback(
    async (quotaId: string, quota: Required<SchemaQuotaEdit>, conditions: Condition | null) => {
      if (!setItem || !Array.isArray(item.quotas)) {
        return false
      }

      const currentQuota = item.quotas.find((itemQuota) => itemQuota.quotaId === quotaId)
      if (!currentQuota) {
        return false
      }

      const metadataChanged = currentQuota.quotaGoal !== quota.goal || currentQuota.quotaName !== quota.name
      if (metadataChanged && !(await updateQuota(quotaId, quota))) {
        return false
      }

      setItem({
        ...item,
        quotas: item.quotas.map((itemQuota) =>
          itemQuota.quotaId === quotaId
            ? { ...itemQuota, conditions: conditions ?? [], quotaGoal: quota.goal, quotaName: quota.name }
            : itemQuota,
        ),
      })

      return true
    },
    [item, setItem, updateQuota],
  )

  return (
    <Quotas
      campaignCountsByQuotaId={campaignCountsByQuotaId}
      isCreatingQuota={isCreatingQuota}
      isFetchingQuotas={isFetching}
      isUpdatingQuota={isUpdatingQuota}
      item={item}
      personalizationList={personalizationList}
      onAddOrganizationQuota={handleAddOrganizationQuota}
      onCreateQuota={handleCreateQuota}
      onDeleteQuota={handleDeleteQuota}
      onSaveConditions={handleSaveConditions}
      onSaveMetadata={handleSaveMetadata}
      onSaveQuota={handleSaveQuota}
    />
  )
}

export default QuotasContainer
